import type { ComponentType } from 'react';

import type { Behörighet } from 'src/layouts/nav-permissions';
import { harBehörighetsgrupp } from 'src/layouts/nav-permissions';
import type { OrganisationsTyp, Verksamhetsinriktning, IRäkenskapsår, IRoll } from 'src/types/nya/common';

import InkorgKort from './kort/inkorg-kort';
import EkonomiKort from './kort/ekonomi-kort';
import LönKort from './kort/lon-kort';
import BokslutKort from './kort/bokslut-kort';
import BarnKort from './kort/barn-kort';
import SchemaKort from './kort/schema-kort';
import FörskolepengKort from './kort/forskolepeng-kort';
import StyrelseKort from './kort/styrelse-kort';
import StädningKort from './kort/stadning-kort';
import ÄrendenKort from './kort/arenden-kort';

// ----------------------------------------------------------------------

export type KortProps = {
  orgId: string;
  räkenskapsår: IRäkenskapsår | undefined;
};

export type KortDefinition = {
  id: string;
  orgTyper?: OrganisationsTyp[];
  verksamhet?: Verksamhetsinriktning[];
  behörighet?: Behörighet;
  komponent: ComponentType<KortProps>;
  ordning: number;
};

// ----------------------------------------------------------------------

/**
 * Kortkatalogen. Varje post anger vilka organisationstyper, verksamhetsinriktningar och
 * behörighetsgrupper den gäller för. `synligaKort()` filtrerar katalogen och returnerar
 * de kort som matchar den aktuella kontexten.
 *
 * Ordningen styr sorteringen inom den filtrerade listan — kort med lägre ordning visas
 * först. Förskole-korten har ordning 10–60 för att hamna före de universella (100+).
 */
export const KORT_KATALOG: KortDefinition[] = [
  // ----- Förskola-specifika kort -----
  {
    id: 'barn',
    verksamhet: ['FÖRÄLDRAKOOPERATIV_FÖRSKOLA'],
    komponent: BarnKort,
    ordning: 10,
  },
  {
    id: 'schema',
    verksamhet: ['FÖRÄLDRAKOOPERATIV_FÖRSKOLA'],
    komponent: SchemaKort,
    ordning: 20,
  },
  {
    id: 'förskolepeng',
    verksamhet: ['FÖRÄLDRAKOOPERATIV_FÖRSKOLA'],
    behörighet: 'ekonomi',
    komponent: FörskolepengKort,
    ordning: 30,
  },
  {
    id: 'styrelse-förskola',
    verksamhet: ['FÖRÄLDRAKOOPERATIV_FÖRSKOLA'],
    behörighet: 'styrelseledamot',
    komponent: StyrelseKort,
    ordning: 40,
  },
  {
    id: 'städning',
    verksamhet: ['FÖRÄLDRAKOOPERATIV_FÖRSKOLA'],
    komponent: StädningKort,
    ordning: 50,
  },
  {
    id: 'ärenden',
    verksamhet: ['FÖRÄLDRAKOOPERATIV_FÖRSKOLA'],
    komponent: ÄrendenKort,
    ordning: 60,
  },

  // ----- Universella kort (alla orgtyper) -----
  {
    id: 'inkorg',
    komponent: InkorgKort,
    ordning: 100,
  },
  {
    id: 'ekonomi',
    behörighet: 'ekonomi',
    komponent: EkonomiKort,
    ordning: 110,
  },
  {
    id: 'lön',
    behörighet: 'ekonomi',
    komponent: LönKort,
    ordning: 120,
  },
  {
    id: 'bokslut',
    behörighet: 'ekonomi',
    komponent: BokslutKort,
    ordning: 130,
  },
];

// ----------------------------------------------------------------------

export function synligaKort(
  katalog: KortDefinition[],
  orgTyp: OrganisationsTyp | 'PRIVATPERSON' | undefined,
  verksamhet: Verksamhetsinriktning | null | undefined,
  roller: IRoll[] | undefined
): KortDefinition[] {
  return katalog
    .filter((kort) => {
      if (kort.orgTyper && orgTyp && orgTyp !== 'PRIVATPERSON') {
        if (!kort.orgTyper.includes(orgTyp as OrganisationsTyp)) return false;
      }

      if (kort.verksamhet) {
        if (!verksamhet || !kort.verksamhet.includes(verksamhet)) return false;
      }

      if (kort.behörighet) {
        if (!harBehörighetsgrupp(roller, kort.behörighet)) return false;
      }

      return true;
    })
    .sort((a, b) => a.ordning - b.ordning);
}
